import React from 'react';
import { Bell } from 'lucide-react';
import { Card } from './TicketingShell';
import Linkify from './Linkify';
import { formatMoney } from '../../utils/ticketing';

/** Sticky sidebar: selected pass, quantity, total and the organiser's note. */
const OrderSummary = ({ pass, qty, currency, onRemove, discount, action, note, children, extras = 0 }) => {
  const unit = pass?.price || 0;
  const subtotal = unit * (qty || 0);
  const total = Math.max(0, subtotal - (discount?.amount || 0)) + (extras || 0);
  return (
    <div className="space-y-5 lg:sticky lg:top-6">
      <Card>
        <h2 className="px-6 pt-6 text-xl font-bold">Order Summary</h2>
        {pass ? (
          <div className="flex items-start justify-between gap-3 border-b px-6 py-5">
            <div className="min-w-0">
              <p className="font-semibold">{pass.name}</p>
              <p className="mt-0.5 text-sm text-gray-500">
                {qty} Qty
                {onRemove && (
                  <>
                    <span className="mx-2">|</span>
                    <button type="button" onClick={onRemove} className="font-medium text-[var(--tk-primary)] underline">Delete</button>
                  </>
                )}
              </p>
            </div>
            <p className="shrink-0 font-semibold tabular-nums">{pass.type === 'PAID' ? formatMoney(subtotal, currency) : 'Free'}</p>
          </div>
        ) : (
          <p className="border-b px-6 py-5 text-sm text-gray-500">No pass selected yet.</p>
        )}
        {extras > 0 && (
          <div className="flex justify-between border-b px-6 py-3 text-sm">
            <span>Selected options</span>
            <span className="tabular-nums">{formatMoney(extras, currency)}</span>
          </div>
        )}
        {discount?.amount > 0 && (
          <div className="flex justify-between border-b px-6 py-3 text-sm text-green-700">
            <span>Discount{discount.code ? ` (${discount.code})` : ''}</span>
            <span className="tabular-nums">−{formatMoney(discount.amount, currency)}</span>
          </div>
        )}
        <div className="flex items-center justify-between px-6 py-5">
          <span className="text-sm font-bold uppercase tracking-wide">Total amount</span>
          <span className="text-2xl font-bold tabular-nums">{total > 0 ? formatMoney(total, currency) : 'Free'}</span>
        </div>
        {children}
        {action && <div className="px-6 pb-6">{action}</div>}
      </Card>
      {note && (
        <Card className="p-6">
          <p className="mb-2 flex items-center gap-3 text-lg font-semibold text-[var(--tk-accent)]">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[color-mix(in_srgb,var(--tk-accent)_12%,white)]">
              <Bell className="h-5 w-5" />
            </span>
            Note
          </p>
          <p className="whitespace-pre-line text-sm leading-relaxed text-gray-700"><Linkify text={note} /></p>
        </Card>
      )}
    </div>
  );
};

export default OrderSummary;
